import { Box, Code, Flex, Grid, Heading, SimpleGrid, Stack, Text, Wrap } from '@chakra-ui/react';
import * as m from 'motion/react-m';
import { useEffect, useState } from 'react';

import { tracking } from '#features/analytics';
import { landingContrastCss } from '#features/discord-lfg-admin/styles';
import { FaqAccordion } from '#features/faq/components/FaqAccordion';
import { GridGlowBackground } from '#features/landing/components/Landing/GridGlowBackground';
import { Link } from '#features/routing';
import { Button, Link as UiLink } from '#features/ui';
import {
  BOT_BANNERS,
  BOT_FAQ,
  BOT_INVITE_URL,
  COMMANDS,
  DISCORD_BOT_LIST_URL,
  FEATURES,
  GAMES,
  PRICING,
  STATS,
  STEPS,
  SUPPORT_DISCORD_URL,
  SUPPORT_EMAIL,
  TOP_GG_VOTE_URL,
} from '../../data';
import { BotFooter } from '../BotFooter';
import { BotHeader } from '../BotHeader';

// Discord blurple as the page accent: the landing sells a Discord product, so it
// carries Discord's colour the same way game pages carry their game's accent.
const ACCENT = '#5865F2';
const ACCENT_SOFT = '#8E9AFF';

// Subtly elevated dark band — matches the main landing's SectionBand so the bot
// page reads as part of the same product family.
const BAND_ELEVATED =
  'linear-gradient(180deg, hsla(228, 11.6%, 11.5%, 1) 0%, hsla(228, 11.6%, 9.5%, 1) 100%)';

const track = (action: string) => tracking.event({ action, category: 'discord_bot_click' });

// The game landing routes are static files (cs2.tsx, valorant.tsx, …), so the router's
// `to` union has no "$gameSlug" pattern to interpolate. TanStack still resolves the
// pattern + params correctly at runtime (same behaviour AllGames relies on); this cast
// keeps that one dynamic usage typed instead of sprinkling ts-expect-error in JSX.
const GameLandingLink = Link as unknown as React.FC<{
  to: string;
  params: Record<string, string>;
  title: string;
  children: React.ReactNode;
}>;

/** Full-bleed background band: background stretches edge-to-edge, content stays in the grid. */
const SectionBand = ({
  background,
  children,
}: {
  background: string;
  children: React.ReactNode;
}) => (
  <Box
    position="relative"
    w="100vw"
    ml="calc(50% - 50vw)"
    mr="calc(50% - 50vw)"
    background={background}
  >
    <Box
      position="absolute"
      inset={0}
      pointerEvents="none"
      background={`radial-gradient(ellipse 80% 60% at 50% 0%, ${ACCENT}14 0%, transparent 70%)`}
    />
    <Box
      position="relative"
      maxW={{ base: '640px', lg: '1200px' }}
      mx="auto"
      px={{ base: 4, md: 6, lg: 8 }}
    >
      {children}
    </Box>
  </Box>
);

const SectionHeader = ({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) => (
  <Box textAlign="center" mb={{ base: 8, lg: 10 }}>
    <Text
      textStyle="badge"
      color="purple.300"
      textTransform="uppercase"
      letterSpacing="0.15em"
      mb={3}
    >
      {eyebrow}
    </Text>
    <Heading
      as="h2"
      fontWeight="900"
      lineHeight="1.05"
      fontSize={{ base: '3xl', lg: '5xl' }}
      textTransform="uppercase"
      letterSpacing="-0.02em"
    >
      {title}
    </Heading>
    {subtitle && (
      <Text mt={4} textStyle={{ base: 'sm', md: 'md' }} color="fg.muted" maxW="620px" mx="auto">
        {subtitle}
      </Text>
    )}
  </Box>
);

const ExternalButton = ({
  href,
  action,
  children,
  ...rest
}: {
  href: string;
  action: string;
  children: React.ReactNode;
} & React.ComponentProps<typeof Button>) => (
  <Button asChild onClick={() => track(action)} {...rest}>
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  </Button>
);

// Real LFG cards generated by the bot, fanned + staggered like the main landing's
// LfgBanners section: proof of output right in the hero.
const BANNER_CARDS = [
  {
    src: BOT_BANNERS[0],
    x: '5%',
    y: '20%',
    rotate: '-12deg',
    z: 1,
    times: [0, 0.08, 0.75, 0.85, 1],
    opacity: [0, 1, 1, 0, 0],
    scale: [0.85, 1, 1, 0.9, 0.9],
  },
  {
    src: BOT_BANNERS[1],
    x: '10%',
    y: '8%',
    rotate: '-2deg',
    z: 2,
    times: [0, 0.05, 0.15, 0.75, 0.85, 1],
    opacity: [0, 0, 1, 1, 0, 0],
    scale: [0.85, 0.85, 1, 1, 0.9, 0.9],
  },
  {
    src: BOT_BANNERS[2],
    x: '18%',
    y: '-5%',
    rotate: '8deg',
    z: 3,
    times: [0, 0.12, 0.22, 0.75, 0.85, 1],
    opacity: [0, 0, 1, 1, 0, 0],
    scale: [0.85, 0.85, 1, 1, 0.9, 0.9],
  },
];

const BannerFan = () => (
  <Box position="relative" w="100%" maxW="520px" mx="auto" style={{ aspectRatio: '4 / 3' }}>
    <Box
      position="absolute"
      top="50%"
      left="50%"
      w="120%"
      h="120%"
      ml="-60%"
      mt="-60%"
      pointerEvents="none"
      asChild
    >
      <m.div
        style={{
          borderRadius: '50%',
          background: `radial-gradient(circle, ${ACCENT}40 0%, transparent 60%)`,
          filter: 'blur(60px)',
        }}
        animate={{ opacity: [0.6, 1, 0.6], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
    </Box>
    {BANNER_CARDS.map((card) => (
      <Box
        key={card.src}
        position="absolute"
        top="16%"
        left="0"
        w="85%"
        zIndex={card.z}
        style={{ transform: `translate(${card.x}, ${card.y}) rotate(${card.rotate})` }}
      >
        <m.img
          src={card.src}
          alt="LFG post card generated by the Teamplay Discord LFG bot"
          loading="lazy"
          style={{
            display: 'block',
            width: '100%',
            borderRadius: '20px',
            boxShadow: '0 30px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.1)',
          }}
          animate={{ opacity: card.opacity, scale: card.scale }}
          transition={{ duration: 6, times: card.times, repeat: Infinity, ease: 'easeInOut' }}
        />
      </Box>
    ))}
  </Box>
);

const HERO_FACTS = ['Free forever', 'Live in ~2 minutes', '18+ games', 'Web admin dashboard'];

const Hero = () => (
  <Flex direction={{ base: 'column', lg: 'row' }} alignItems="center" gap={{ base: 10, lg: 14 }}>
    <Stack
      flex="1.1"
      gap={{ base: 6, lg: 7 }}
      alignItems={{ base: 'center', lg: 'flex-start' }}
      textAlign={{ base: 'center', lg: 'left' }}
    >
      <Flex
        display="inline-flex"
        alignItems="center"
        gap={2}
        px={3}
        py={1.5}
        borderRadius="full"
        border="1px solid"
        borderColor="whiteAlpha.200"
        bg="whiteAlpha.50"
      >
        <Box w="7px" h="7px" borderRadius="full" bg={ACCENT} flexShrink={0} />
        <Text
          textStyle="badge"
          color="fg.emphasized"
          letterSpacing="0.08em"
          textTransform="uppercase"
        >
          Discord LFG bot by Teamplay.gg
        </Text>
      </Flex>

      <Heading
        as="h1"
        fontWeight="900"
        fontSize={{ base: '4xl', md: '6xl', xl: '6xl' }}
        lineHeight="0.95"
        letterSpacing="-0.03em"
        textTransform="uppercase"
      >
        <Text as="span" display="block">
          The Discord LFG bot that keeps
        </Text>
        <Text
          as="span"
          display="block"
          style={{
            background: `linear-gradient(100deg, ${ACCENT} 0%, ${ACCENT_SOFT} 55%, ${ACCENT} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          your server active 24/7
        </Text>
      </Heading>

      <Text textStyle={{ base: 'md', md: 'lg' }} lineHeight="1.7" color="fg.muted" maxW="560px">
        Teamplay LFG automates looking-for-group in your gaming community. Members post squads,
        schedule sessions, get DM reminders and drop into auto-created voice channels – while
        cross-server discovery fills lobbies faster than any single server could.
      </Text>

      <Flex gap={3} flexDir={{ base: 'column', sm: 'row' }} w={{ base: '100%', sm: 'auto' }} mt={1}>
        <ExternalButton
          href={BOT_INVITE_URL}
          action="hero_invite"
          size="lg"
          minW="220px"
          w={{ base: '100%', sm: 'auto' }}
          style={{ boxShadow: `0 0 26px ${ACCENT}55` }}
        >
          Add to Discord – Free
        </ExternalButton>
        <Button
          asChild
          size="lg"
          variant="outline"
          color="fg.emphasized"
          borderColor="whiteAlpha.200"
          w={{ base: '100%', sm: 'auto' }}
          onClick={() => track('hero_dashboard')}
          _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
        >
          <Link to="/{-$locale}/discord-lfg-bot/admin">Open web dashboard</Link>
        </Button>
        <Button
          asChild
          size="lg"
          variant="ghost"
          color="fg.muted"
          w={{ base: '100%', sm: 'auto' }}
          _hover={{ bg: 'whiteAlpha.50', color: 'fg.emphasized' }}
        >
          <a href="#how-it-works">See how it works</a>
        </Button>
      </Flex>

      <Flex gap={2.5} flexWrap="wrap" justifyContent={{ base: 'center', lg: 'flex-start' }} mt={1}>
        {HERO_FACTS.map((fact) => (
          <Flex
            key={fact}
            alignItems="center"
            gap={2}
            px={3}
            py={2}
            borderRadius="full"
            border="1px solid"
            borderColor="whiteAlpha.100"
            bg="whiteAlpha.50"
          >
            <Box
              w="7px"
              h="7px"
              borderRadius="full"
              flexShrink={0}
              style={{ background: `linear-gradient(180deg, ${ACCENT_SOFT} 0%, ${ACCENT} 100%)` }}
            />
            <Text textStyle="title" color="fg.emphasized">
              {fact}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Stack>

    <Box flex="1" w="100%" display={{ base: 'none', md: 'block' }}>
      <BannerFan />
    </Box>
  </Flex>
);

const StatsStrip = () => (
  <Box
    as="section"
    aria-label="Teamplay LFG in numbers"
    position="relative"
    py={{ base: 6, md: 8 }}
    px={{ base: 4, md: 10 }}
    borderRadius="2xl"
    border="1px solid"
    borderColor="whiteAlpha.100"
    background="linear-gradient(135deg, hsla(228, 11.6%, 12%, 0.6) 0%, hsla(228, 11.6%, 10%, 0.4) 100%)"
    style={{ backdropFilter: 'blur(10px)' }}
  >
    <Flex
      direction="row"
      gap={{ base: 4, md: 0 }}
      justifyContent="space-around"
      alignItems="center"
      flexWrap="wrap"
    >
      {STATS.map((stat, index) => (
        <Flex
          key={stat.label}
          direction="column"
          alignItems="center"
          gap={1}
          flex={{ base: '0 0 40%', md: 1 }}
          minW="70px"
          asChild
        >
          <m.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
          >
            <Text
              fontSize={{ base: '2xl', md: '4xl' }}
              fontWeight="900"
              lineHeight="1"
              letterSpacing="-0.02em"
              style={{
                background:
                  'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.62) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {stat.value}
              {stat.suffix}
            </Text>
            <Text
              textStyle="caption"
              color="fg.muted"
              textTransform="uppercase"
              letterSpacing="0.08em"
              textAlign="center"
            >
              {stat.label}
            </Text>
          </m.div>
        </Flex>
      ))}
    </Flex>
  </Box>
);

const Features = () => (
  <Box id="features" css={{ scrollMarginTop: '80px' }}>
    <SectionHeader
      eyebrow="Features"
      title="Everything your server needs"
      subtitle="One bot, instant setup. Your members start finding teammates in seconds – and the bot keeps working while your mods sleep."
    />
    <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} gap={{ base: 4, md: 5 }}>
      {FEATURES.map((feature) => (
        <Box
          key={feature.title}
          p={6}
          borderRadius="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
          transition="border-color 0.2s, transform 0.2s"
          _hover={{ borderColor: 'whiteAlpha.300', transform: 'translateY(-2px)' }}
        >
          <Text textStyle="2xl" mb={3} aria-hidden>
            {feature.icon}
          </Text>
          <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
            {feature.title}
          </Heading>
          <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
            {feature.description}
          </Text>
        </Box>
      ))}
    </SimpleGrid>
  </Box>
);

const Commands = () => (
  <Flex
    id="commands"
    css={{ scrollMarginTop: '80px' }}
    direction={{ base: 'column', lg: 'row' }}
    alignItems="center"
    gap={{ base: 10, lg: 14 }}
  >
    <Box flex="1" w="100%" textAlign={{ base: 'center', lg: 'left' }}>
      <Text
        textStyle="badge"
        color="purple.300"
        textTransform="uppercase"
        letterSpacing="0.15em"
        mb={3}
      >
        Slash commands
      </Text>
      <Heading
        as="h2"
        fontWeight="900"
        fontSize={{ base: '2xl', md: '4xl' }}
        lineHeight="1.1"
        maxW="480px"
        mx={{ base: 'auto', lg: 0 }}
        textTransform="uppercase"
        letterSpacing="-0.02em"
      >
        Commands your members already know
      </Heading>
      <Text
        textStyle="md"
        color="fg.muted"
        lineHeight="1.7"
        maxW="460px"
        mx={{ base: 'auto', lg: 0 }}
        mt={4}
      >
        No syntax to memorize: every command opens a guided, clickable wizard right in Discord. Pick
        a game, set the details, and the bot posts a rich LFG card for you.
      </Text>
    </Box>

    <Box flex="1" w="100%">
      <Box
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.200"
        overflow="hidden"
        background="linear-gradient(165deg, hsla(228, 11.6%, 13%, 0.9) 0%, hsla(228, 11.6%, 8%, 0.8) 100%)"
      >
        <Flex
          px={5}
          py={3}
          gap={2}
          alignItems="center"
          borderBottom="1px solid"
          borderColor="whiteAlpha.100"
        >
          {['#f87171', '#fbbf24', '#34d399'].map((dot) => (
            <Box key={dot} w="10px" h="10px" borderRadius="full" style={{ background: dot }} />
          ))}
          <Text textStyle="xs" color="fg.subtle" ml={2} fontWeight="600">
            Teamplay LFG
          </Text>
        </Flex>
        <Stack p={{ base: 4, md: 6 }} gap={3}>
          {COMMANDS.map((command) => (
            <Flex
              key={command.cmd}
              gap={3}
              alignItems="baseline"
              flexWrap="wrap"
              px={4}
              py={3}
              borderRadius="xl"
              border="1px solid"
              borderColor="whiteAlpha.100"
              bg="whiteAlpha.50"
            >
              <Code colorPalette="purple" textStyle="strong">
                {command.cmd}
              </Code>
              {command.adminOnly && (
                <Text
                  as="span"
                  textStyle="micro"
                  color="fg.emphasized"
                  px={2}
                  py={0.5}
                  borderRadius="full"
                  bg="whiteAlpha.200"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  Admin
                </Text>
              )}
              <Text textStyle="sm" color="fg.muted" flexBasis="100%">
                {command.description}
              </Text>
            </Flex>
          ))}
        </Stack>
      </Box>
    </Box>
  </Flex>
);

const HowItWorks = () => (
  <Box id="how-it-works" css={{ scrollMarginTop: '80px' }}>
    <SectionHeader
      eyebrow="Quick start"
      title="Live in under 2 minutes"
      subtitle="Slash commands for members, a web dashboard for admins – nothing to install beyond the invite."
    />
    <Grid
      templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
      gap={{ base: 4, md: 5 }}
      maxW="900px"
      mx="auto"
    >
      {STEPS.map((step, index) => (
        <Box
          key={step.title}
          p={6}
          borderRadius="2xl"
          border="1px solid"
          borderColor="whiteAlpha.100"
          background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
        >
          <Flex
            w="34px"
            h="34px"
            borderRadius="full"
            alignItems="center"
            justifyContent="center"
            fontWeight="800"
            fontSize="md"
            color="white"
            mb={4}
            style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_SOFT} 100%)` }}
          >
            {index + 1}
          </Flex>
          <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
            {step.title}
          </Heading>
          <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
            {step.description}
          </Text>
        </Box>
      ))}
    </Grid>
    <Flex justifyContent="center" mt={{ base: 8, md: 10 }}>
      <ExternalButton
        href={BOT_INVITE_URL}
        action="setup_invite"
        size="lg"
        minW="240px"
        style={{ boxShadow: `0 0 26px ${ACCENT}55` }}
      >
        Add to Discord – it&apos;s free
      </ExternalButton>
    </Flex>
  </Box>
);

const Games = () => (
  <Box id="games" css={{ scrollMarginTop: '80px' }}>
    <SectionHeader
      eyebrow="Supported games"
      title={`${GAMES.length} games & counting`}
      subtitle="From tactical shooters to MOBAs – the roster comes straight from the teamplay.gg catalog and grows regularly."
    />
    <Wrap gap={2} justify="center" maxW="900px" mx="auto">
      {GAMES.map((game, index) => (
        <Box key={game.slug} asChild>
          <m.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.02, duration: 0.3 }}
          >
            {/* Real link to the game landing — distributes authority to the site's
                highest-intent SEO pages and gives each game a keyword-rich anchor. */}
            <GameLandingLink
              to="/{-$locale}/$gameSlug"
              params={{ gameSlug: game.slug }}
              title={`${game.name} LFG – find teammates`}
            >
              <Flex
                px={4}
                py={2.5}
                borderRadius="full"
                border="1px solid"
                borderColor="whiteAlpha.150"
                bg="whiteAlpha.50"
                textStyle="title"
                color="fg.emphasized"
                transition="all 0.2s"
                _hover={{ borderColor: ACCENT, bg: 'whiteAlpha.100', color: 'white' }}
              >
                {game.name}
              </Flex>
            </GameLandingLink>
          </m.div>
        </Box>
      ))}
    </Wrap>
    <Text textAlign="center" textStyle="sm" color="fg.subtle" mt={6}>
      Don&apos;t see your game?{' '}
      <Text asChild color="purple.300" fontWeight="600">
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Teamplay LFG - game request`}
          onClick={() => track('game_request')}
        >
          Request it here
        </a>
      </Text>{' '}
      – we add new titles regularly.
    </Text>
  </Box>
);

const Pricing = () => (
  <Box id="pricing" css={{ scrollMarginTop: '80px' }}>
    <SectionHeader
      eyebrow="Pricing"
      title="Start free. Scale when ready."
      subtitle="Core LFG features are free forever. Upgrade only if you need more."
    />
    <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: 4, md: 5 }} alignItems="stretch">
      {PRICING.map((tier) => (
        <Flex
          key={tier.title}
          direction="column"
          gap={3}
          p={{ base: 5, md: 6 }}
          borderRadius="2xl"
          border="1px solid"
          borderColor={tier.highlighted ? `${ACCENT}66` : 'whiteAlpha.100'}
          background="linear-gradient(165deg, hsla(228, 11.6%, 14%, 0.85) 0%, hsla(228, 11.6%, 9%, 0.6) 100%)"
          boxShadow={tier.highlighted ? `0 0 34px ${ACCENT}22` : undefined}
        >
          <Text
            textStyle="micro"
            color={tier.highlighted ? ACCENT_SOFT : 'purple.300'}
            textTransform="uppercase"
            letterSpacing="0.1em"
          >
            {tier.badge}
          </Text>
          <Heading as="h3" fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800">
            {tier.title}
          </Heading>
          <Text textStyle="sm" color="fg.emphasized" lineHeight="1.6">
            {tier.description}
          </Text>
          <Text textStyle="sm" color="fg.muted" lineHeight="1.6">
            {tier.details}
          </Text>
          <Box mt="auto" pt={3}>
            {tier.cta ? (
              <ExternalButton
                href={tier.cta.href}
                action={`pricing_${tier.title.toLowerCase().replaceAll(/\W+/g, '_')}`}
                size="md"
                w="100%"
                variant={tier.highlighted ? 'solid' : 'outline'}
                color={tier.highlighted ? undefined : 'fg.emphasized'}
                borderColor={tier.highlighted ? undefined : 'whiteAlpha.200'}
              >
                {tier.cta.label}
              </ExternalButton>
            ) : (
              <Button size="md" w="100%" variant="outline" disabled>
                Coming soon
              </Button>
            )}
          </Box>
        </Flex>
      ))}
    </SimpleGrid>
  </Box>
);

const Faq = () => {
  const [openKey, setOpenKey] = useState<string | null>(BOT_FAQ[0]?.question ?? null);
  const items = BOT_FAQ.map((item) => ({
    key: item.question,
    question: item.question,
    answer: item.answer,
  }));

  return (
    <Box id="faq" css={{ scrollMarginTop: '80px' }} as="section" aria-label="FAQ">
      <SectionHeader
        eyebrow="FAQ"
        title="Frequently asked questions"
        subtitle="Everything you need to know about the Teamplay LFG bot for Discord."
      />
      <Box maxW="800px" mx="auto">
        <FaqAccordion
          items={items}
          openKey={openKey}
          onToggle={(key) => setOpenKey((prev) => (prev === key ? null : key))}
        />
        <Text textStyle="sm" color="fg.subtle" mt={6} textAlign="center">
          More details in the{' '}
          <UiLink
            to="/{-$locale}/discord-lfg-app/privacy-policy"
            color="purple.300"
            fontWeight="600"
          >
            Privacy Policy
          </UiLink>{' '}
          and{' '}
          <UiLink
            to="/{-$locale}/discord-lfg-app/terms-and-conditions"
            color="purple.300"
            fontWeight="600"
          >
            Terms of Use
          </UiLink>
          .
        </Text>
      </Box>
    </Box>
  );
};

const Community = () => (
  <Box id="community" css={{ scrollMarginTop: '80px' }}>
    <SectionHeader
      eyebrow="Community"
      title="Join & support us"
      subtitle="Connect with us, share your ideas, and help more communities discover Teamplay LFG."
    />
    <SimpleGrid columns={{ base: 1, md: 3 }} gap={{ base: 4, md: 5 }}>
      <Box
        p={6}
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
      >
        <Text textStyle="2xl" mb={3} aria-hidden>
          💬
        </Text>
        <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
          Need help or have an idea?
        </Heading>
        <Text textStyle="sm" color="fg.muted" lineHeight="1.6" mb={4}>
          Join the support server for setup help, or send us game requests, feature ideas and
          feedback by email.
        </Text>
        <Stack gap={2}>
          <ExternalButton
            href={SUPPORT_DISCORD_URL}
            action="community_support"
            size="sm"
            variant="outline"
            color="fg.emphasized"
            borderColor="whiteAlpha.200"
          >
            Join support server
          </ExternalButton>
          <ExternalButton
            href={`mailto:${SUPPORT_EMAIL}?subject=Teamplay LFG – feedback`}
            action="community_feedback"
            size="sm"
            variant="ghost"
            color="fg.muted"
          >
            {SUPPORT_EMAIL}
          </ExternalButton>
        </Stack>
      </Box>

      <Box
        p={6}
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
      >
        <Text textStyle="2xl" mb={3} aria-hidden>
          ⭐
        </Text>
        <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
          Love it? Vote!
        </Heading>
        <Text textStyle="sm" color="fg.muted" lineHeight="1.6" mb={4}>
          A quick vote helps more gaming communities discover Teamplay LFG on bot listing sites.
          There&apos;s even a /vote command.
        </Text>
        <Stack gap={2}>
          <ExternalButton
            href={TOP_GG_VOTE_URL}
            action="community_topgg"
            size="sm"
            variant="outline"
            color="fg.emphasized"
            borderColor="whiteAlpha.200"
          >
            Vote on Top.gg
          </ExternalButton>
          <ExternalButton
            href={DISCORD_BOT_LIST_URL}
            action="community_dbl"
            size="sm"
            variant="ghost"
            color="fg.muted"
          >
            Vote on Discord Bot List
          </ExternalButton>
        </Stack>
      </Box>

      <Box
        p={6}
        borderRadius="2xl"
        border="1px solid"
        borderColor="whiteAlpha.100"
        background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.7) 0%, hsla(228, 11.6%, 9%, 0.4) 100%)"
      >
        <Text textStyle="2xl" mb={3} aria-hidden>
          🎮
        </Text>
        <Heading as="h3" fontSize="md" fontWeight="800" mb={2}>
          Powered by Teamplay.gg
        </Heading>
        <Text textStyle="sm" color="fg.muted" lineHeight="1.6" mb={4}>
          Teamplay.gg is a free LFG platform for finding teammates across {GAMES.length}+ games. The
          Discord bot extends that experience directly into your server.
        </Text>
        <Button
          asChild
          size="sm"
          variant="outline"
          color="fg.emphasized"
          borderColor="whiteAlpha.200"
          onClick={() => track('community_platform')}
          _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
        >
          <Link to="/{-$locale}">Visit Teamplay.gg</Link>
        </Button>
      </Box>
    </SimpleGrid>
  </Box>
);

const FinalCta = () => (
  <Box position="relative" w="100%">
    <Box
      position="absolute"
      top="-70px"
      left="50%"
      transform="translateX(-50%)"
      w="360px"
      h="360px"
      background={`radial-gradient(circle, ${ACCENT}26 0%, transparent 70%)`}
      pointerEvents="none"
    />
    <Flex
      direction="column"
      alignItems="center"
      gap={5}
      py={{ base: 12, md: 16 }}
      px={{ base: 6, md: 12 }}
      background="linear-gradient(165deg, hsla(228, 11.6%, 15%, 0.85) 0%, hsla(228, 11.6%, 9%, 0.5) 100%)"
      border="1px solid"
      borderColor="whiteAlpha.100"
      borderRadius="3xl"
      textAlign="center"
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top={0}
        left="10%"
        right="10%"
        h="1px"
        background={`linear-gradient(90deg, transparent, ${ACCENT}88, transparent)`}
      />
      <Heading
        as="h2"
        fontSize={{ base: '3xl', md: '5xl' }}
        lineHeight="1.02"
        maxW="640px"
        fontWeight="900"
        textTransform="uppercase"
        letterSpacing="-0.02em"
      >
        Your server, active 24/7
      </Heading>
      <Text textStyle="md" color="fg.muted" maxW="480px">
        Add Teamplay LFG for free and let your members find their next squad tonight.
      </Text>
      <Flex gap={3} flexDir={{ base: 'column', sm: 'row' }} w={{ base: '100%', sm: 'auto' }} mt={2}>
        <ExternalButton
          href={BOT_INVITE_URL}
          action="footer_invite"
          size="lg"
          minW="240px"
          w={{ base: '100%', sm: 'auto' }}
          style={{ boxShadow: `0 0 28px ${ACCENT}59` }}
        >
          Add to Discord – Free
        </ExternalButton>
        <ExternalButton
          href={SUPPORT_DISCORD_URL}
          action="footer_support"
          size="lg"
          variant="outline"
          color="fg.emphasized"
          borderColor="whiteAlpha.200"
          w={{ base: '100%', sm: 'auto' }}
          _hover={{ borderColor: 'whiteAlpha.400', bg: 'whiteAlpha.50' }}
        >
          Join our Discord
        </ExternalButton>
      </Flex>
    </Flex>
  </Box>
);

/** Persistent "Add to Discord" pinned to the bottom on mobile once the hero scrolls away. */
const StickyMobileInvite = () => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 640);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box
      display={{ base: 'block', md: 'none' }}
      position="fixed"
      bottom={0}
      left={0}
      right={0}
      zIndex={1200}
      px={4}
      pt={3}
      pb="calc(env(safe-area-inset-bottom, 0px) + 12px)"
      background="linear-gradient(to top, hsl(228, 11.6%, 6%) 60%, transparent)"
      transform={show ? 'translateY(0)' : 'translateY(120%)'}
      opacity={show ? 1 : 0}
      transition="transform 0.3s ease, opacity 0.3s ease"
      pointerEvents={show ? 'auto' : 'none'}
    >
      <Button
        w="100%"
        size="lg"
        style={{ boxShadow: `0 0 24px ${ACCENT}55` }}
        onClick={() => track('sticky_invite')}
        asChild
      >
        <a href={BOT_INVITE_URL} target="_blank" rel="noopener noreferrer">
          Add to Discord – Free
        </a>
      </Button>
    </Box>
  );
};

export const DiscordLfgBotLanding = () => (
  <Stack
    position="relative"
    m="0 auto"
    gap={0}
    w="100%"
    maxW={{ base: '640px', lg: '1200px' }}
    px={{ base: 4, md: 6, lg: 8 }}
    // Cancels MainLayout's top padding so the page's own sticky header sits flush
    // under the viewport edge (the header itself carries no margins — see BotHeader).
    mt={{ base: -5, lg: -5 }}
    mb={4}
    // Landing-scoped contrast + accent overrides (same trick as Landing/BuildWithUs):
    // brighten muted/subtle foreground for AA on dark, tune purple accent tokens.
    css={landingContrastCss}
  >
    {/* Dedicated LFG-bot header (the route suppresses the global one). */}
    <BotHeader />

    {/* Hero — full-bleed animated grid-glow background, content stays in the 1200px grid. */}
    <Box position="relative" w="100vw" ml="calc(50% - 50vw)" mr="calc(50% - 50vw)">
      <GridGlowBackground glowColors={[ACCENT, '#8E2DE2', ACCENT]}>
        <Box
          maxW={{ base: '640px', lg: '1200px' }}
          mx="auto"
          px={{ base: 4, md: 6, lg: 8 }}
          py={{ base: 10, lg: 16 }}
        >
          <Hero />
        </Box>
      </GridGlowBackground>
    </Box>

    {/* Social-proof numbers as the first beat under the hero. */}
    <Box py={6}>
      <StatsStrip />
    </Box>

    {/* Features — elevated colour band */}
    <SectionBand background={BAND_ELEVATED}>
      <Box py={{ base: 12, md: 16 }}>
        <Features />
      </Box>
    </SectionBand>

    {/* Slash commands — base */}
    <Box py={{ base: 12, md: 16 }}>
      <Commands />
    </Box>

    {/* How it works — elevated colour band */}
    <SectionBand background={BAND_ELEVATED}>
      <Box py={{ base: 12, md: 16 }}>
        <HowItWorks />
      </Box>
    </SectionBand>

    {/* Supported games — base, crawlable links to every game landing */}
    <Box py={{ base: 12, md: 16 }}>
      <Games />
    </Box>

    {/* Pricing — elevated colour band */}
    <SectionBand background={BAND_ELEVATED}>
      <Box py={{ base: 12, md: 16 }}>
        <Pricing />
      </Box>
    </SectionBand>

    {/* FAQ — base (FAQPage JSON-LD is emitted from the route head) */}
    <Box py={{ base: 12, md: 16 }}>
      <Faq />
    </Box>

    {/* Community — elevated colour band */}
    <SectionBand background={BAND_ELEVATED}>
      <Box py={{ base: 12, md: 16 }}>
        <Community />
      </Box>
    </SectionBand>

    {/* Final CTA — base */}
    <Box py={{ base: 12, md: 16 }}>
      <FinalCta />
    </Box>

    {/* Dedicated LFG-bot footer (the route suppresses the global one). */}
    <BotFooter />

    <StickyMobileInvite />
  </Stack>
);
